import path from "node:path";

import winston from "winston";

// Define custom log levels
const customLevels = {
  levels: {
    error: 0,
    warn: 1,
    info: 2,
    http: 3,
    debug: 4,
  },
  colors: {
    error: "red",
    warn: "yellow",
    info: "green",
    http: "magenta",
    debug: "blue",
  },
};

// Add colors to winston
winston.addColors(customLevels.colors);

// Define log format
const logFormat = winston.format.combine(
  winston.format.timestamp({ format: "YYYY-MM-DD HH:mm:ss" }),
  winston.format.errors({ stack: true }),
  winston.format.json(),
  winston.format.prettyPrint(),
);

// Console format for development
const consoleFormat = winston.format.combine(
  winston.format.colorize({ all: true }),
  winston.format.timestamp({ format: "YYYY-MM-DD HH:mm:ss" }),
  winston.format.printf(info => {
    const { timestamp, level, message, stack, ...meta } = info;
    let log = `${String(timestamp)} [${level}]: ${String(message)}`;

    if (stack && typeof stack === "string") {
      log += `\n${stack}`;
    }

    if (Object.keys(meta).length > 0) {
      log += `\n${JSON.stringify(meta, null, 2)}`;
    }

    return log;
  }),
);

// Create logger instance
const logger = winston.createLogger({
  levels: customLevels.levels,
  level: process.env.LOG_LEVEL ?? "info",
  format: logFormat,
  defaultMeta: { service: "covalent-backend" },
  transports: [
    // Console transport for development
    new winston.transports.Console({
      format: consoleFormat,
      silent: process.env.NODE_ENV === "test",
    }),

    // File transport for errors
    new winston.transports.File({
      filename: path.join(process.cwd(), "logs", "error.log"),
      level: "error",
      maxsize: 5_242_880, // 5MB
      maxFiles: 5,
    }),

    // File transport for all logs
    new winston.transports.File({
      filename: path.join(process.cwd(), "logs", "combined.log"),
      maxsize: 5_242_880, // 5MB
      maxFiles: 10,
    }),
  ],
});

// Create logs directory if it doesn't exist
import fs from "node:fs";
const logsDir = path.join(process.cwd(), "logs");
if (!fs.existsSync(logsDir)) {
  fs.mkdirSync(logsDir, { recursive: true });
}

// Export logger with additional utility methods
export { logger };

// HTTP request logger middleware
export const httpLogger = winston.createLogger({
  levels: customLevels.levels,
  level: "http",
  format: winston.format.combine(
    winston.format.timestamp(),
    winston.format.json(),
  ),
  defaultMeta: { service: "covalent-backend-http" },
  transports: [
    new winston.transports.File({
      filename: path.join(process.cwd(), "logs", "http.log"),
      maxsize: 5_242_880, // 5MB
      maxFiles: 5,
    }),
  ],
});

// Error logging helper
export const logError = (error: Error, context?: Record<string, unknown>) => {
  logger.error(error.message, {
    stack: error.stack,
    context,
    timestamp: new Date().toISOString(),
  });
};

// Request logging helper
export const logRequest = (
  method: string,
  url: string,
  userId?: string,
  responseTime?: number,
) => {
  httpLogger.http("HTTP Request", {
    method,
    url,
    userId,
    responseTime,
    timestamp: new Date().toISOString(),
  });
};

// Authentication logging helper
export const logAuth = (
  action: string,
  email: string,
  success: boolean,
  reason?: string,
) => {
  logger.info(`Auth: ${action}`, {
    email,
    success,
    reason,
    timestamp: new Date().toISOString(),
  });
};
